import { FC, lazy, Suspense } from "react";
import { withStockStamp } from "@/components/stock-stamp-hoc/StockStampHOC";
import { Box } from "@mui/system";
import { CircularProgress, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { UPGRADE_LOCATIONS } from "@/lib/tracking";
import useUpgradeCta from "src/hooks/useUpgradeCta";
const MUILineChart = lazy(() => import("@/components/line-chart/MUILineChart"));

/**
 * Squeeze Potential Score stamp (stock overview page).
 *
 * Mirrors the Shorts stamp layout: headline value, context line,
 * 3-month sparkline. Clicking through lands on the per-stock
 * Positioning tab (StockStamps supplies the link).
 *
 * Advanced gating: the API sends band-only payloads to lower tiers, so
 * `gated` stamps show the band + a lock and an obfuscated sparkline —
 * the real number never reaches the client.
 */
interface PropsTypes {
    overviewPage?: boolean;
    row?: any;
    /** null score + band set = gated teaser. */
    score: number | null;
    band: string;
    shortScore?: number | null;
    optionsScore?: number | null;
    ts?: number[];
    gated?: boolean;
    obfuscated?: boolean;
}

const BAND_COLORS: Record<string, string> = {
    high: "#ef4444",
    elevated: "#F59E0B",
    low: "#31aba6",
};

// Neutral placeholder wiggle for the gated sparkline — MUILineChart's
// obfuscated mode blurs it anyway; the shape carries no information.
const GATED_TS = [42, 45, 41, 48, 52, 47, 55, 51, 58, 54, 60, 57];

const SqueezePotential: FC<PropsTypes> = ({ score, band, shortScore, optionsScore, ts, gated, obfuscated }) => {
    const color = BAND_COLORS[band] ?? "#31aba6";
    const bandLabel = band ? band.charAt(0).toUpperCase() + band.slice(1) : "—";
    const unlock = useUpgradeCta(UPGRADE_LOCATIONS.SQUEEZE_STAMP);

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ height: "20%" }}>
                <p style={{ color: "var(--text-3, #8a8f97)", textTransform: "initial" }}>
                    Potential: <span style={{ color, fontWeight: 700 }}>{bandLabel}</span>
                </p>
            </Box>

            <Box sx={{ height: "20%", width: "90%", marginX: "auto", display: "flex", justifyContent: "center", alignItems: "baseline", gap: 0.5 }}>
                {gated ? (
                    // The way in. The stamp itself is a link to the options platform, so the
                    // click stops here instead of following it.
                    <Box
                        component="button"
                        type="button"
                        title={unlock.isVisitor ? "Create a free account" : "Part of ORTEX Advanced"}
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            unlock.onClick(e);
                        }}
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, background: "none", border: 0, p: 0, cursor: "pointer", color: "var(--accent-text, #31aba6)" }}
                    >
                        <LockOutlinedIcon sx={{ fontSize: 14 }} />
                        <Typography variant="moduleContentTitle" sx={{ textTransform: "none", textDecoration: "underline" }}>
                            {unlock.isVisitor ? "Sign up" : "Get Advanced"}
                        </Typography>
                    </Box>
                ) : score == null ? (
                    <>
                        <LockOutlinedIcon sx={{ fontSize: 14, color: "var(--text-3, #8a8f97)", alignSelf: "center" }} />
                        <Typography variant="moduleContentTitle" sx={{ color: "var(--text-3, #8a8f97)", textTransform: "none" }}>
                            Advanced
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="moduleContentTitle" sx={{ color, fontWeight: 800 }}>
                            {Math.round(score)}
                        </Typography>
                        <Typography variant="caption">/ 100</Typography>
                    </>
                )}
            </Box>

            <Box sx={{ height: "60%" }}>
                <Suspense
                    fallback={
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                            <CircularProgress size={30} />
                        </div>
                    }
                >
                    <MUILineChart data={gated || !ts?.length ? GATED_TS : ts} labels={["-3m", "-2m", "-1m"]} labelsAlign={"left"} obfuscated={obfuscated || gated} />
                </Suspense>
            </Box>
        </Box>
    );
};

export default withStockStamp(SqueezePotential, "band", { title: "Squeeze Potential" }, "Squeeze");
